import { useState, useMemo } from "react";
import { canDo } from "@shared/roles";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, Copy, FileText, Send, Filter,
} from "lucide-react";
import type { Devis, LigneDevis } from "@shared/schema";
import type { Profile } from "@shared/schema";
import { printDevis } from "./print";

const STATUT_LABELS: Record<string, string> = {
  brouillon: "Brouillon",
  envoye: "Envoyé",
  accepte: "Accepté",
  refuse: "Refusé",
  expire: "Expiré",
};

const STATUT_CLASSES: Record<string, string> = {
  brouillon: "bg-secondary text-secondary-foreground",
  envoye: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  accepte: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  refuse: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  expire: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function calcMontantHT(lignes: LigneDevis[], remiseGlobale: string | null): number {
  const lines = Array.isArray(lignes) ? lignes : [];
  const sousTotal = lines.reduce((acc, l) => {
    const pu = Number(l.puHt) || 0;
    const qty = Number(l.quantite) || 0;
    const rem = Number(l.remise) || 0;
    return acc + pu * qty * (1 - rem / 100);
  }, 0);
  const rg = Number(remiseGlobale) || 0;
  return sousTotal * (1 - rg / 100);
}

export default function DevisList() {
  const [, navigate] = useLocation();
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterCommercial, setFilterCommercial] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<Devis | null>(null);

  const canEdit = canDo(profile?.accessRole) || profile?.role === "planification" || profile?.role === "commercial";

  const { data: devisList = [], isLoading } = useQuery<Devis[]>({
    queryKey: ["/api/devis"],
    queryFn: async () => {
      const res = await authFetch("/api/devis");
      if (!res.ok) throw new Error("Erreur chargement devis");
      return res.json();
    },
  });

  const { data: profiles = [] } = useQuery<Profile[]>({
    queryKey: ["/api/profiles"],
    queryFn: async () => {
      const res = await authFetch("/api/profiles");
      if (!res.ok) throw new Error("Erreur profils");
      return res.json();
    },
  });

  const { data: logoData } = useQuery<{ url: string | null }>({
    queryKey: ["/api/settings/logo"],
    queryFn: async () => (await authFetch("/api/settings/logo")).json(),
  });
  const logoUrl = logoData?.url || undefined;

  const commercials = useMemo(
    () => profiles.filter(p => ["admin", "commercial", "planification"].includes(p.role)),
    [profiles]
  );

  const filtered = useMemo(() => {
    return devisList.filter(d => {
      const txt = `${d.numero} ${d.societe || ""} ${d.contactNom || ""} ${d.objet || ""}`.toLowerCase();
      if (search && !txt.includes(search.toLowerCase())) return false;
      if (filterStatut !== "all" && d.statut !== filterStatut) return false;
      if (filterCommercial !== "all" && d.commercialId !== filterCommercial) return false;
      return true;
    });
  }, [devisList, search, filterStatut, filterCommercial]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/devis/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur suppression");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: "Devis supprimé" });
      setDeleteTarget(null);
    },
    onError: () => toast({ title: "Erreur suppression", variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/devis/${id}/duplicate`, { method: "POST" });
      if (!res.ok) throw new Error("Erreur duplication");
      return res.json();
    },
    onSuccess: (dup: Devis) => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: `Devis dupliqué : ${dup.numero}` });
      navigate(`/devis/${dup.id}/editer`);
    },
    onError: () => toast({ title: "Erreur duplication", variant: "destructive" }),
  });

  const finaliserMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/devis/${id}`, {
        method: "PUT",
        body: JSON.stringify({ statut: "envoye" }),
      });
      if (!res.ok) throw new Error("Erreur finalisation");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: "Devis finalisé et marqué Envoyé" });
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const getCommercialName = (id: string | null) => {
    if (!id) return "—";
    const p = profiles.find(p => p.id === id);
    return p ? `${p.firstName} ${p.lastName}`.trim() : "—";
  };

  const getAuteur = (id: string | null | undefined): Profile | undefined => {
    if (!id) return undefined;
    return profiles.find(p => p.id === id);
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Devis</h1>
          <Badge variant="secondary" className="text-sm tabular-nums">{devisList.length}</Badge>
        </div>
        {canEdit && (
          <Button onClick={() => navigate("/devis/nouveau")} data-testid="button-new-devis">
            <Plus className="w-4 h-4 mr-2" /> Nouveau devis
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher numéro, société, objet…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-devis"
          />
        </div>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-40" data-testid="select-filter-statut">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {Object.entries(STATUT_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCommercial} onValueChange={setFilterCommercial}>
          <SelectTrigger className="w-44" data-testid="select-filter-commercial">
            <SelectValue placeholder="Commercial" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous commerciaux</SelectItem>
            {commercials.map(p => (
              <SelectItem key={p.id} value={p.id}>
                {`${p.firstName} ${p.lastName}`.trim()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Numéro</TableHead>
              <TableHead>Destinataire</TableHead>
              <TableHead>Objet</TableHead>
              <TableHead>Commercial</TableHead>
              <TableHead className="w-12">Auteur</TableHead>
              <TableHead className="text-right">Montant HT</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center h-32 text-muted-foreground">Chargement…</TableCell>
              </TableRow>
            ) : filtered.map(d => {
              const lignes = (d.lignes as unknown as LigneDevis[]) || [];
              const montantHT = calcMontantHT(lignes, d.remiseGlobale);
              return (
                <TableRow
                  key={d.id}
                  className="group hover:bg-secondary/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/devis/${d.id}/editer`)}
                  data-testid={`row-devis-${d.id}`}
                >
                  <TableCell className="py-2 font-mono font-semibold text-primary">
                    <div className="flex items-center gap-2">
                      {d.numero}
                      {(d as any).origine === "portail_partenaire" && (
                        <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border-0">
                          Portail
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="font-medium">{d.societe || d.contactNom || "—"}</div>
                    {d.contactNom && d.societe && (
                      <div className="text-xs text-muted-foreground">{d.contactNom}</div>
                    )}
                  </TableCell>
                  <TableCell className="py-2 max-w-[200px] truncate text-muted-foreground">
                    {d.objet || "—"}
                  </TableCell>
                  <TableCell className="py-2 text-sm">{getCommercialName(d.commercialId)}</TableCell>
                  <TableCell className="py-2">
                    {(() => {
                      const auteur = getAuteur((d as any).createdBy);
                      if (!auteur) {
                        return (
                          <Avatar className="h-7 w-7 border grayscale opacity-50" data-testid={`avatar-auteur-${d.id}`}>
                            <AvatarFallback className="text-xs">—</AvatarFallback>
                          </Avatar>
                        );
                      }
                      const initiales = `${auteur.firstName?.[0] ?? ""}${auteur.lastName?.[0] ?? ""}`.toUpperCase();
                      const nomComplet = `${auteur.firstName} ${auteur.lastName}`.trim();
                      return (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Avatar className="h-7 w-7 border shadow-sm" data-testid={`avatar-auteur-${d.id}`}>
                                <AvatarImage src={auteur.avatarUrl || undefined} />
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                                  {initiales || "?"}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">{nomComplet}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-2 text-right tabular-nums font-medium">
                    {montantHT > 0 ? `${montantHT.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : "—"}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge
                      variant="secondary"
                      className={`text-xs ${STATUT_CLASSES[d.statut || "brouillon"] || ""}`}
                    >
                      {STATUT_LABELS[d.statut || "brouillon"]}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2 text-sm text-muted-foreground">
                    {d.createdAt ? new Date(d.createdAt).toLocaleDateString("fr-FR") : "—"}
                  </TableCell>
                  <TableCell className="py-2 w-10" onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`button-actions-devis-${d.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => navigate(`/devis/${d.id}/editer`)}>
                          <Pencil className="w-4 h-4 mr-2" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => printDevis(d, lignes, getCommercialName(d.commercialId), logoUrl, getAuteur((d as any).createdBy) ? `${getAuteur((d as any).createdBy)!.firstName} ${getAuteur((d as any).createdBy)!.lastName}`.trim() : null)}>
                          <FileText className="w-4 h-4 mr-2" /> Générer PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => duplicateMutation.mutate(d.id)}>
                          <Copy className="w-4 h-4 mr-2" /> Dupliquer
                        </DropdownMenuItem>
                        {canEdit && d.statut === "brouillon" && (
                          <DropdownMenuItem onClick={() => finaliserMutation.mutate(d.id)}>
                            <Send className="w-4 h-4 mr-2" /> Finaliser
                          </DropdownMenuItem>
                        )}
                        {canEdit && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={() => setDeleteTarget(d)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  {search || filterStatut !== "all" || filterCommercial !== "all"
                    ? "Aucun devis ne correspond aux filtres"
                    : "Aucun devis pour l'instant"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le devis {deleteTarget?.numero} ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
